import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Copy, Check, ExternalLink, Facebook, Twitter, Instagram, Share2, X } from 'lucide-react';
import { toast } from 'sonner';

// Ícono original de WhatsApp (SVG personalizado para que quede igual de elegante que el resto)
const WhatsappIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);

export default function ShareModal({ open, onClose, shareUrl }) {
  const [copied, setCopied] = useState(false);

  // FIX: Si no le pasamos shareUrl, asume que queremos compartir la app general
  const isAppShare = !shareUrl;
  const finalUrl = shareUrl || "https://dinamiclassapp.com.ar";
  const defaultText = isAppShare 
    ? "¡Profe, tenés que probar DinamiClass! Genera dinámicas de clase en segundos. 🚀" 
    : "¡Mirá este plan de clases genial que armé con DinamiClass! 🚀";

  const handleCopy = () => {
    navigator.clipboard.writeText(finalUrl);
    setCopied(true);
    toast.success('Enlace copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTo = (platform) => {
    const encodedUrl = encodeURIComponent(finalUrl);
    const encodedText = encodeURIComponent(defaultText);

    const links = {
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      instagram: `https://www.instagram.com/` // Instagram no tiene API web directa
    };

    if (platform === 'instagram') {
      navigator.clipboard.writeText(finalUrl);
      toast.success('Enlace copiado. ¡Pegalo en tu historia o biografía!');
      setTimeout(() => window.open(links.instagram, '_blank'), 1500);
    } else {
      window.open(links[platform], '_blank');
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* [&>button]:hidden oculta la cruz nativa para usar la tuya */}
      <DialogContent className="bg-slate-900 border-slate-800 text-white sm:max-w-md p-6 z-[100] [&>button]:hidden">
        
        <DialogHeader className="mb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                <Share2 className="text-blue-400" size={20} />
              </div>
              <div className="text-left">
                <DialogTitle className="text-xl font-bold">
                  {isAppShare ? 'Recomendar DinamiClass' : 'Compartir dinámica'}
                </DialogTitle>
                <DialogDescription className="text-slate-400 text-sm mt-1">
                  {isAppShare ? 'Invita a otros docentes a sumarse' : 'Cualquier persona con este enlace podrá verla'}
                </DialogDescription>
              </div>
            </div>
            <button onClick={onClose} className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
              <X size={16} strokeWidth={2.5}/>
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          
          {/* Input con Botón Copiar */}
          <div className="flex items-center gap-2 bg-slate-950 p-1.5 rounded-xl border border-slate-800 focus-within:border-slate-600 transition-colors">
            <input 
              type="text" 
              value={finalUrl} 
              readOnly 
              className="flex-1 bg-transparent border-none text-slate-300 text-sm px-3 focus:outline-none w-full"
            />
            <button 
              onClick={handleCopy}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all shrink-0 ${
                copied ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-600 hover:bg-blue-500 text-white'
              }`}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>

          {/* Botonera de Redes Sociales */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">O compartir directo en</p>
            <div className="grid grid-cols-4 gap-3">
              <button onClick={() => shareTo('whatsapp')} className="flex flex-col items-center gap-2 group">
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 group-hover:bg-[#25D366] group-hover:text-white group-hover:border-[#25D366] transition-all shadow-sm">
                  <WhatsappIcon size={20} />
                </div>
                <span className="text-[10px] font-medium text-slate-500 group-hover:text-slate-300 transition-colors">WhatsApp</span>
              </button>
              
              <button onClick={() => shareTo('twitter')} className="flex flex-col items-center gap-2 group">
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 group-hover:bg-black group-hover:text-white group-hover:border-slate-600 transition-all shadow-sm">
                  <Twitter size={18} />
                </div>
                <span className="text-[10px] font-medium text-slate-500 group-hover:text-slate-300 transition-colors">X (Twitter)</span>
              </button>
              
              <button onClick={() => shareTo('facebook')} className="flex flex-col items-center gap-2 group">
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 group-hover:bg-[#1877F2] group-hover:text-white group-hover:border-[#1877F2] transition-all shadow-sm">
                  <Facebook size={20} />
                </div>
                <span className="text-[10px] font-medium text-slate-500 group-hover:text-slate-300 transition-colors">Facebook</span>
              </button>
              
              <button onClick={() => shareTo('instagram')} className="flex flex-col items-center gap-2 group">
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 group-hover:bg-gradient-to-tr group-hover:from-[#f09433] group-hover:via-[#e6683c] group-hover:to-[#bc1888] group-hover:text-white group-hover:border-transparent transition-all shadow-sm">
                  <Instagram size={20} />
                </div>
                <span className="text-[10px] font-medium text-slate-500 group-hover:text-slate-300 transition-colors">Instagram</span>
              </button>
            </div>
          </div>

          {!isAppShare && (
            <>
              <div className="w-full h-px bg-slate-800/80"></div>
              <div className="flex flex-col gap-4">
                <a 
                  href={finalUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors w-fit"
                >
                  <ExternalLink size={16} />
                  Ver cómo lo va a ver el receptor
                </a>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-xs text-blue-200/70 leading-relaxed">
                  <strong className="text-blue-400">¿Sabías qué?</strong> El enlace es público y no tiene fecha de vencimiento. Quien lo abra verá la dinámica en modo lectura y un banner invitándolo a usar DinamiClass.
                </div>
              </div>
            </>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
